import type { CountryCode } from 'libphonenumber-js';
import { memo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { lazily } from 'react-lazily';

import { postcodeSampleMap, validatePostcode } from '@login/helpers';
import type { Country } from '@login/helpers/arrays';
import { getCountriesNormalize } from '@login/helpers/options';

import styles from './address-fields.module.scss';

const { Insert, Select } = lazily(() => import('core/generic/elements/form'));

export type AddressLabels = {
	country: string;
	countryPlaceholder: string;
	countryRequired: string;
	addressLine1: string;
	addressLine1Required: string;
	addressLine2: string;
	addressLine3: string;
	city: string;
	cityRequired: string;
	postcode: string;
	postcodeRequired: string;
	/** Receives a `postcodeSample` interpolation. */
	postcodeInvalid: (postcodeSample: string) => string;
};

export type AddressDefaults = {
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	city?: string;
	postcode?: string;
};

type ResolveFieldError = (formMessage?: string, apiKey?: string) => string;

type AddressFieldsProps = {
	labels: AddressLabels;
	countries: Country[];
	defaults?: AddressDefaults;
	/** Country pinned to the top of the list. */
	priorityCountry?: string;
	resolveError?: ResolveFieldError;
};

const EMPTY_DEFAULTS: AddressDefaults = {};

/**
 * Country, three address lines, city and postcode.
 *
 * Extracted from full-register and required-info, which carried ~200 near
 * identical lines each. The postcode rule reads the selected country through a
 * local `useWatch`, so typing an address line no longer re-renders the rest of
 * the form.
 */
const AddressFieldsInner = ({
	labels,
	countries,
	defaults = EMPTY_DEFAULTS,
	priorityCountry = 'IT',
	resolveError,
}: AddressFieldsProps) => {
	const {
		control,
		register,
		trigger,
		getValues,
		formState: { errors },
	} = useFormContext();

	const selectedCountry = useWatch({ control, name: 'address.countryCode' });
	const postcodeSample =
		postcodeSampleMap[selectedCountry as CountryCode] || '';

	// Postcode format is country-specific, so an already-entered postcode has
	// to be re-validated when the country changes. The current postcode is read
	// with getValues rather than watched, so typing one does not re-render.
	useEffect(() => {
		if (getValues('address.postcode')) trigger('address.postcode');
	}, [selectedCountry, trigger, getValues]);

	const errorFor = (formMessage?: string, apiKey?: string) =>
		resolveError?.(formMessage, apiKey) ?? formMessage ?? '';

	const addressErrors = errors.address as
		| Record<string, { message?: string }>
		| undefined;

	return (
		<div className={styles.addressSection}>
			<Select
				{...register('address.countryCode', {
					required: labels.countryRequired,
				})}
				name="address.countryCode"
				label={labels.country}
				control={control}
				trigger={trigger}
				id="address.countryCode"
				placeholder={labels.countryPlaceholder}
				error={errorFor(
					addressErrors?.countryCode?.message,
					'Address.CountryCode'
				)}
				errorIcon="AlertCircleFilled"
				required={true}
				options={getCountriesNormalize(
					countries,
					'shortName',
					priorityCountry
				)}
			/>
			<Insert
				{...register('address.addressLine1', {
					required: labels.addressLine1Required,
				})}
				defaultValue={defaults.addressLine1 ?? ''}
				id="address.addressLine1"
				label={labels.addressLine1}
				required={true}
				error={errorFor(
					addressErrors?.addressLine1?.message,
					'Address.AddressLine1'
				)}
				errorIcon="AlertCircleFilled"
			/>
			<Insert
				{...register('address.addressLine2')}
				defaultValue={defaults.addressLine2 ?? ''}
				id="address.addressLine2"
				label={labels.addressLine2}
				required={false}
				optional={true}
				error={addressErrors?.addressLine2?.message ?? ''}
				errorIcon="AlertCircleFilled"
			/>
			<Insert
				{...register('address.addressLine3')}
				defaultValue={defaults.addressLine3 ?? ''}
				id="address.addressLine3"
				label={labels.addressLine3}
				required={false}
				optional={true}
				error={addressErrors?.addressLine3?.message ?? ''}
				errorIcon="AlertCircleFilled"
			/>
			<Insert
				{...register('address.city', { required: labels.cityRequired })}
				defaultValue={defaults.city ?? ''}
				id="address.city"
				label={labels.city}
				required={true}
				error={errorFor(addressErrors?.city?.message, 'Address.City')}
				errorIcon="AlertCircleFilled"
			/>
			<Insert
				{...register('address.postcode', {
					required: labels.postcodeRequired,
					validate: (value: string) =>
						validatePostcode(
							selectedCountry as CountryCode,
							value
						) || labels.postcodeInvalid(postcodeSample),
				})}
				defaultValue={defaults.postcode ?? ''}
				id="address.postcode"
				label={labels.postcode}
				required={true}
				error={errorFor(
					addressErrors?.postcode?.message,
					'Address.Postcode'
				)}
				errorIcon="AlertCircleFilled"
			/>
		</div>
	);
};

export const AddressFields = memo(AddressFieldsInner);

export default AddressFields;
